import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, MapPin, CalendarDays, Ticket, ChevronLeft, Landmark } from "lucide-react";
import api from "@/lib/api";
import AIContentBlock from "@/components/AIContentBlock";
import useTranslated from "@/hooks/useTranslated";
import { EventCard } from "@/components/home/EventsSection";

export default function EventDetail() {
  const { eventSlug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const descT = useTranslated(`event-desc-${eventSlug}`, data ? [data.event.description] : []);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/events/${eventSlug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [eventSlug]);

  if (notFound) return <div className="py-24 text-center text-slate-500" data-testid="event-not-found">Event not found. <Link to="/events" className="text-pink-600 font-bold">Browse all events</Link></div>;
  if (!data) return <div className="min-h-[50vh] flex items-center justify-center"><span className="eq-bars" style={{ height: 36 }}><span /><span /><span /><span /><span /></span></div>;

  const { event: ev, venue, related } = data;
  const dateStr = ev.date ? new Date(`${ev.date}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : `${ev.month} ${ev.day}`;
  const ticketUrl = ev.ticket_url || `https://www.google.com/search?q=${encodeURIComponent(`${ev.title} ${ev.city} tickets`)}`;

  return (
    <div data-testid={`event-page-${eventSlug}`}>
      <div className="relative h-64 md:h-80">
        <img src={ev.image} alt={ev.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          <Link to="/events" data-testid="event-back-link" className="inline-flex items-center gap-1 text-xs font-bold text-pink-300 hover:text-pink-200 mb-2">
            <ChevronLeft className="w-3.5 h-3.5" /> All events
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            {ev.tags?.map((t) => <span key={t} className="px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-white text-[10px] font-black uppercase tracking-wide">{t}</span>)}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white mt-2 flex items-center gap-3 flex-wrap">
            {ev.title}
            {ev.status === "verified" && <BadgeCheck className="w-8 h-8 text-sky-400" title="Verified by organizer or official source" />}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2">
            <p className="text-slate-600 leading-relaxed" data-testid="event-description">{descT[0] || ev.description}</p>
            <AIContentBlock pageKey={`event-${eventSlug}`} topic={`${ev.title} in ${ev.city}, ${ev.country} — what makes this gathering special for music travelers`} compact />
          </div>
          <div className="space-y-5">
            <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="event-facts-card">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-blue-600" /> Date</p>
              <p className="text-sm text-slate-800 font-bold" data-testid="event-date">{dateStr}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-5 mb-3 flex items-center gap-2"><Landmark className="w-4 h-4 text-amber-600" /> Venue</p>
              {venue ? (
                <Link to={`/venues/${venue.id}`} data-testid="event-venue-link" className="text-sm font-bold text-pink-600 hover:text-pink-700">{venue.name} ›</Link>
              ) : (
                <p className="text-sm text-slate-800 font-bold" data-testid="event-venue-text">{ev.venue}</p>
              )}
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-5 mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-pink-600" /> Location</p>
              <p className="text-sm text-slate-800 font-bold">
                {ev.city}, {ev.country_slug ? <Link to={`/places/${ev.country_slug}`} className="text-pink-600 hover:underline">{ev.country}</Link> : ev.country}
              </p>
              <a href={ticketUrl} target="_blank" rel="noopener noreferrer" data-testid="event-ticket-btn" className="gradient-btn mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white text-sm font-black">
                <Ticket className="w-4 h-4" /> {ev.ticket_url ? "Get tickets" : "Find tickets"}
              </a>
              {!ev.ticket_url && <p className="text-[10px] text-slate-400 mt-2 text-center">Opens a web search — always buy from the organizer's official site.</p>}
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-10" data-testid="event-related-section">
            <h2 className="font-display text-2xl font-black text-slate-900 mb-5">More Events You Might Like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((r) => <EventCard key={r.id} ev={r} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
